import { useEffect, useMemo, useRef, useState } from 'react'
import { ToggleTypes, useWidgetContext } from '../../context/WidgetContext'
import type { SpeechOrbAdapter, SpeechOrbMountArgs } from '../../types/lib-config'

const resolvePath = (root: any, path: string) =>
  path
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === 'object' ? (acc as any)[key] : undefined), root)

const isSpeechOrbAdapter = (value: unknown): value is SpeechOrbAdapter =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as any).mount === 'function'

export const SpeechConversationPanel = () => {
  const { agent, config, toggle } = useWidgetContext()
  const hostRef = useRef<HTMLDivElement | null>(null)
  const instanceIdRef = useRef(`speech-orb-${Math.random().toString(36).slice(2, 10)}`)
  const toggleRef = useRef(toggle)
  const [isHostReady, setIsHostReady] = useState(false)

  useEffect(() => {
    toggleRef.current = toggle
  }, [toggle])

  const directAdapter = (config as any).speechOrb
  const globalKey =
    typeof (config as any).speechOrbGlobal === 'string' &&
    (config as any).speechOrbGlobal.trim()
      ? (config as any).speechOrbGlobal.trim()
      : 'OsirusAgentSpeechOrb'

  const adapter = useMemo(() => {
    if (typeof window === 'undefined') return null

    const globalAdapter = resolvePath(window as any, globalKey)
    if (isSpeechOrbAdapter(directAdapter)) return directAdapter
    if (isSpeechOrbAdapter(globalAdapter)) return globalAdapter
    return null
  }, [directAdapter, globalKey])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const host = hostRef.current
    if (!host) return

    if (!adapter) {
      setIsHostReady(false)
      return
    }

    const closeSpeech = () => {
      toggleRef.current(ToggleTypes.ai, true)
      toggleRef.current(ToggleTypes.widget, false)
    }

    const mountArgs: SpeechOrbMountArgs = {
      container: host,
      mode: 'speech',
      instanceId: instanceIdRef.current,
      agentId: agent.id,
      agentName: agent.name || agent.companyName || 'Agent',
      primaryColor: agent.customColor || undefined,
      onClose: closeSpeech,
    }

    let cleanup: void | (() => void)
    let cancelled = false

    Promise.resolve()
      .then(() => adapter.mount(mountArgs))
      .then((result) => {
        if (cancelled) {
          if (typeof result === 'function') {
            try {
              result()
            } catch {
              // ignore
            }
          }
          return
        }
        cleanup = result
        setIsHostReady(true)
      })
      .catch(() => {
        if (!cancelled) setIsHostReady(false)
      })

    return () => {
      cancelled = true
      try {
        if (typeof cleanup === 'function') {
          cleanup()
        } else {
          adapter.unmount?.(host)
        }
      } catch {
        // ignore
      }
    }
  }, [adapter, agent.id])

  return (
    <div className="osirus-agent-panel-frame osirus-speech-conversation-panel">
      <div ref={hostRef} className="osirus-speech-host-root" />
      {!isHostReady && (
        <div className="osirus-speech-host-fallback">
          <p>Speech conversation host is not registered.</p>
        </div>
      )}
    </div>
  )
}
